import { useParams } from "react-router-dom";
import {
  useGetArticleGenerator,
  useUpdateArticleGenerator,
} from "@packages/api/madlenAPI";
import { getGetArticleGeneratorQueryKey } from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { TextEditor } from "@features/tools/components/tool_components/text-editor/TextEditor";
import LoadingContainer from "@packages/components/Loading";
import type { Descendant } from "slate";
import { useTranslation } from "react-i18next";
import ErrorComponent from "@features/tools/components/tool_components/ErrorComponent";
import { transformEmbeddedTemplatesToAIContent } from "@features/tools/utils/template-transformer";

function getSlateValue(article: any): Descendant[] {
  let slateContent: Descendant[];

  if (article?.articleContentJson) {
    slateContent = Array.isArray(article.articleContentJson)
      ? article.articleContentJson
      : [article.articleContentJson];
  } else if (article?.rawArticle) {
    slateContent = [
      {
        type: "paragraph",
        children: [{ text: article.rawArticle }],
      },
    ];
  } else {
    slateContent = [
      {
        type: "paragraph",
        children: [{ text: "No content available." }],
      },
    ];
  }

  // Transform embedded-template elements to ai-content elements
  return transformEmbeddedTemplatesToAIContent(slateContent);
}

export function ArticleView() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetArticleGenerator(
    Number(id),
    {
      query: {
        enabled: !!id,
        refetchInterval: (data) =>
          data?.state.data?.status === "READY" ? false : 2000,
      },
    },
  );

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: updateArticle } = useUpdateArticleGenerator({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetArticleGeneratorQueryKey(Number(id)),
        });
      },
    },
  });

  const isArticleLoading = isLoading || data?.status !== "READY";

  const handleSave = async (value: Descendant[]) => {
    if (!id) return;
    await updateArticle({
      id: Number(id),
      data: {
        articleContentJson: value as unknown as Record<string, unknown>,
      },
    });
  };

  if (isArticleLoading) {
    return (
      <LoadingContainer
        title={t("articleGenerator.loading")}
        themeColor="teal"
        fullHeight
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("articleGenerator.error.title")}
        description={t("articleGenerator.error.description")}
        retryButton={t("articleGenerator.error.retryButton")}
        refetch={refetch}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 dark:from-gray-900 dark:to-teal-900 flex items-center justify-center">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        <TextEditor
          value={getSlateValue(data)}
          properties={{
            title: data?.title,
            author: data?.authorName,
            createdAt: data?.createdAt,
            imageUrl: data?.imageUrl,
          }}
          onChange={() => {}}
          onSave={handleSave}
        />
      </div>
    </div>
  );
}

export default ArticleView;
